import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';

export default function NewContactsButton({ currentUserId, currentUserName = 'Obchodník', isDarkMode, onContactsAssigned, customClassName, customText }) {
  const [contactsBatches, setContactsBatches] = useState([]);
  const [contactsUser, setContactsUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Queue / waitlist system
  const [inQueue, setInQueue]             = useState(false);
  const [queuePos, setQueuePos]           = useState(0);
  const [queueTimeLeft, setQueueTimeLeft] = useState(20);
  const [queueAhead, setQueueAhead]       = useState([]); // uživatelé přede mnou
  const batchQueueLoaded = useRef(false);
  const handleCloseRef   = useRef(null);
  const loadBatchesRef   = useRef(null);

  // Blikání tlačítka — je-li notifikovaná šarže s dostupnými kontakty
  const [shouldBlink, setShouldBlink] = useState(false);

  useEffect(() => {
    if (!currentUserId) return;
    const checkBlink = async () => {
      try {
        const res = await fetch(`${API_URL}/batch-notify/check?userId=${currentUserId}`);
        if (res.ok) {
          const d = await res.json();
          setShouldBlink(!!d.shouldBlink);
        }
      } catch {}
    };
    checkBlink();
    const id = setInterval(checkBlink, 60000);
    return () => clearInterval(id);
  }, [currentUserId]);

  // Hodnocení šarží (1–5 hvězdiček), sdílené v rámci partnera
  // Struktura: { batchName: { userId: { stars, userName } } }
  const [batchRatings, setBatchRatings] = useState({});

  // Která šarže + hvězda je právě hovered (pro postupné zelené zbarvení)
  const [hoverInfo, setHoverInfo] = useState(null); // { batch, star }

  const setRating = async (batchName, stars) => {
    const batchEntry = { ...(batchRatings[batchName] || {}) };
    if (stars === 0) {
      delete batchEntry[currentUserId];
    } else {
      batchEntry[currentUserId] = { stars, userName: currentUserName };
    }
    const updated = { ...batchRatings, [batchName]: batchEntry };
    setBatchRatings(updated);
    // Uložit na server (sdílené s ostatními obchodníky)
    fetch(`${API_URL}/batch-ratings?userId=${currentUserId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ batchName, stars, userName: currentUserName })
    }).catch(() => {});
  };

  // Odstranil useEffect - načítání až po kliknutí

  const loadContactsBatches = async () => {
    try {
      setLoading(true);
      
      // Načti hodnocení šarží ze serveru souběžně s kontakty
      const ratingsPromise = fetch(`${API_URL}/batch-ratings?userId=${currentUserId}`)
        .then(r => r.ok ? r.json() : null)
        .catch(() => null);

      // Najdi uživatele "kontakty"
      const usersRes = await fetch(`${API_URL}/users?userId=${localStorage.getItem('lastUserId') || ''}`);
      const users = await usersRes.json();
      const contactsUserData = users.find(user => 
        user.email === 'kontakty@vyjedname.cz' ||
        user.name?.toLowerCase().includes('kontakt') || 
        user.email?.toLowerCase().includes('kontakt')
      );
      
      if (!contactsUserData) {
        console.error('❌ Uživatel kontakty@vyjedname.cz nebyl nalezen');
        return;
      }
      
      setContactsUser(contactsUserData);
      
      // Načti nastavení viditelnosti pro aktuálního uživatele + auto-assign config souběžně
      console.log('🔧 Načítám nastavení viditelnosti pro uživatele:', currentUserId);
      const [visibilityRes, autoAssignRes] = await Promise.all([
        fetch(`${API_URL}/batch-visibility/${currentUserId}`),
        fetch(`${API_URL}/batch-autoassign?userId=${currentUserId}`).catch(() => null),
      ]);
      let allowedBatches = [];
      let autoAssignBatches = new Set();
      
      if (visibilityRes.ok) {
        const visibilityData = await visibilityRes.json();
        allowedBatches = visibilityData.visibleBatches || [];
        console.log('✅ Nastavení viditelnosti:', allowedBatches);
      } else {
        console.warn('⚠️ Žádné nastavení viditelnosti, zobrazím všechny šarže');
      }

      if (autoAssignRes && autoAssignRes.ok) {
        const aaCfg = await autoAssignRes.json();
        (aaCfg.batches || []).forEach(b => {
          if (b.autoAssign) autoAssignBatches.add(b.batchName);
        });
      }
      
      // Načti všechny kontakty od uživatele kontakty@vyjedname.cz
      console.log('📊 Načítám všechny kontakty od kontakty@vyjedname.cz (ID:', contactsUserData._id, ')...');
      const contactsRes = await fetch(`${API_URL}/companies?userId=${contactsUserData._id}&limit=15000`);
      
      if (!contactsRes.ok) {
        throw new Error(`Chyba při načítání kontaktů: HTTP ${contactsRes.status}`);
      }
      
      const contactsData = await contactsRes.json();
      const contacts = Array.isArray(contactsData) ? contactsData : (contactsData.companies || []);
      
      console.log('📋 Načteno kontaktů od uživatele kontakty@vyjedname.cz:', contacts.length);
      console.log('📦 Celkový počet kontaktů v DB:', contactsData.totalCount || 'neznámý');
      
      // Seskup podle batchName
      const batches = {};
      contacts.forEach(contact => {
        const batchName = contact.batchName || 'Bez názvu várky';
        if (!batches[batchName]) {
          batches[batchName] = [];
        }
        batches[batchName].push(contact);
      });
      
      // Převeď na array a filtruj podle viditelnosti
      const batchesArray = Object.entries(batches)
        .map(([name, contacts]) => ({ name, contacts, count: contacts.length }))
        .filter(batch => {
          // Zobraz pouze šarže s kontakty
          if (batch.count === 0) return false;
          
          // Přeskoč šarže s automatickým přidělováním
          if (autoAssignBatches.has(batch.name)) return false;
          
          // DŮLEŽITÉ: Pokud nejsou nastavené žádné viditelné šarže, nezobrazuj nic
          if (!allowedBatches || allowedBatches.length === 0) {
            console.log('❌ Žádné povolené šarže - skrývám všechny');
            return false;
          }
          
          // Zobraz pouze povolené šarže
          const isAllowed = allowedBatches.includes(batch.name);
          console.log(`🔍 Šarže "${batch.name}" - povolená: ${isAllowed}`);
          return isAllowed;
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'cs'));
        
      console.log('✅ Finální šarže k zobrazení:', batchesArray.map(b => `${b.name} (${b.count})`));
      
      setContactsBatches(batchesArray);

      // Aplikuj hodnocení ze serveru (sdílená mezi obchodníky)
      const ratingsData = await ratingsPromise;
      if (ratingsData) setBatchRatings(ratingsData);
      
    } catch (error) {
      console.error('Chyba při načítání várek kontaktů:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    setShowModal(false);
    setInQueue(false);
    setQueuePos(0);
    setQueueTimeLeft(20);
    setQueueAhead([]);
    setInitialized(false);
    batchQueueLoaded.current = false;
    if (currentUserId) {
      fetch(`${API_URL}/contacts-queue/leave?userId=${currentUserId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': currentUserId }
      }).catch(() => {});
    }
  }, [currentUserId]);

  // Vždy aktuální reference pro použití uvnitř setInterval callbacků
  handleCloseRef.current  = handleClose;
  loadBatchesRef.current  = loadContactsBatches;

  // Polling stavu fronty každé 2 sekundy (pouze když jsem ve frontě)
  useEffect(() => {
    if (!inQueue || !currentUserId) return;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/contacts-queue/status?userId=${currentUserId}`, {
          headers: { 'x-user-id': currentUserId }
        });
        if (!res.ok) return;
        const d = await res.json();
        if (d.position === 0) { handleCloseRef.current?.(); return; }
        setQueuePos(d.position ?? 1);
        setQueueAhead(d.ahead || []);
        // Právě jsem se dostal na první místo – načti várky
        if (d.position === 1 && !batchQueueLoaded.current) {
          batchQueueLoaded.current = true;
          setQueueTimeLeft(d.remaining ?? 20);
          setLoading(true);
          await loadBatchesRef.current?.();
          setInitialized(true);
          setLoading(false);
        }
      } catch {}
    };
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [inQueue, currentUserId]);

  // Odpočet sekund – tičká pro všechny ve frontě (polling každé 2s ho resetuje ze serveru)
  useEffect(() => {
    if (!inQueue) return;
    const id = setInterval(() => {
      setQueueTimeLeft(t => {
        if (t <= 1) {
          if (queuePos === 1) handleCloseRef.current?.();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [queuePos, inQueue]);

  const handleOpenModal = async () => {
    if (inQueue) { setShowModal(true); return; }
    setShowModal(true);
    batchQueueLoaded.current = false;
    try {
      const joinRes = await fetch(`${API_URL}/contacts-queue/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': currentUserId },
        body: JSON.stringify({ userId: currentUserId, userName: currentUserName })
      });
      const qData = joinRes.ok ? await joinRes.json() : { position: 1, remaining: 20, ahead: [] };
      const myPos = qData.position ?? 1;
      setQueuePos(myPos);
      setQueueAhead(qData.ahead || []);
      setQueueTimeLeft(qData.remaining ?? 20);
      if (myPos === 1) {
        batchQueueLoaded.current = true;
        setLoading(true);
        await loadContactsBatches();
        setInitialized(true);
        setLoading(false);
      }
      setInQueue(true);
    } catch {
      // Fronta nedostupná – zobraz várky přímo bez odpočtu
      setQueuePos(1);
      if (!initialized) {
        setLoading(true);
        await loadContactsBatches();
        setInitialized(true);
        setLoading(false);
      }
    }
  };

  const handleAssignContacts = async (batchName, count = 10) => {
    try {
      setLoading(true);
      
      const batch = contactsBatches.find(b => b.name === batchName);
      if (!batch || batch.count === 0) {
        alert('V této várce nejsou žádné kontakty');
        return;
      }
      
      const contactsToAssign = batch.contacts.slice(0, Math.min(count, batch.count));
      if (contactsToAssign.length === 0) {
        alert('Žádné kontakty k přidělení');
        return;
      }
      
      // Najdi aktuálního uživatele pro získání jména
      const usersRes = await fetch(`${API_URL}/users?userId=${localStorage.getItem('lastUserId') || ''}`);
      const users = await usersRes.json();
      const currentUser = users.find(user => user._id === currentUserId);
      
      if (!currentUser) {
        alert('Aktuální uživatel nebyl nalezen');
        return;
      }

      console.log('👤 Přiděluju kontakty uživateli:', currentUser.name, '(ID:', currentUserId, ')');

      // Přiděl kontakty aktuálnímu uživateli
      const today = new Date();
      const nextActionDate = today.toISOString();
      
      for (const contact of contactsToAssign) {
        // DŮLEŽITÉ: activities záměrně vynecháváme z PUT — GET vrací jen poslední aktivitu
        // (.slice('activities', -1)) a přepis by smazal historii. Novou aktivitu přidáváme
        // separátním POST na /activities, který dělá $push na backendu.
        const { activities: _ignored, ...contactWithoutActivities } = contact;
        const updatedContact = {
          ...contactWithoutActivities,
          assignedTo: currentUserId,
          createdBy: currentUserId,
          assignedBy: currentUser.name,
          batchName: batchName,
          status: "Nové",
          nextActionDate: nextActionDate,
        };

        const response = await fetch(`${API_URL}/companies/${contact._id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'X-User-Id': currentUserId,
            'X-Transfer-Type': 'assignment'
          },
          body: JSON.stringify(updatedContact)
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Chyba při aktualizaci kontaktu:', contact.name, 'Status:', response.status, 'Error:', errorText);
          throw new Error(`Chyba při aktualizaci kontaktu ${contact.name}: ${response.status} - ${errorText}`);
        }

        // Přidej aktivitu separátně přes $push — zachová celou historii aktivit
        await fetch(`${API_URL}/companies/${contact._id}/activities`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: `Přiděleno uživateli ${currentUser.name} - ${today.toLocaleDateString('cs-CZ')}`,
            createdBy: currentUserId,
            nextActionDate: nextActionDate,
          })
        });

        console.log('✅ Kontakt aktualizován:', contact.name);
      }

      alert(`Úspěšně přiděleno ${contactsToAssign.length} kontaktů z várky "${batchName}"`);
      
      // Znovu načti várky
      await loadContactsBatches();
      
      // Informuj nadřazenou komponentu
      if (onContactsAssigned) {
        onContactsAssigned();
      }

      handleClose();

    } catch (error) {
      console.error('Chyba při přidělování kontaktů:', error);
      alert(`Chyba při přidělování kontaktů: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {shouldBlink && !customClassName && (
        <style>{`
          @keyframes batchNotifyBlink {
            0%, 100% { background-color: #16a34a; box-shadow: 0 0 0 0 rgba(22,163,74,0); }
            50% { background-color: #dc2626; box-shadow: 0 0 8px 3px rgba(220,38,38,0.5); }
          }
        `}</style>
      )}
      <button
        onClick={handleOpenModal}
        className={customClassName || `px-4 py-2 rounded font-semibold transition flex items-center gap-2 text-white ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        style={!customClassName ? (
          shouldBlink && !loading
            ? { animation: 'batchNotifyBlink 2s ease-in-out infinite' }
            : { backgroundColor: isDarkMode ? '#15803d' : '#16a34a' }
        ) : {}}
        disabled={loading}
      >
        {loading ? '⏳ Načítám...' : (customText || '📞 Nové kontakty')}
      </button>

      {/* Loading screen */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center shadow-2xl">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
            <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">Načítám kontakty...</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Připravuji dostupné šarže</p>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`w-[600px] max-w-[90vw] max-h-[80vh] rounded-lg shadow-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`p-4 border-b flex justify-between items-center ${
              isDarkMode ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                📦 Dostupné várky kontaktů
              </h3>
              <button
                onClick={handleClose}
                className={`text-gray-500 hover:text-gray-700 ${isDarkMode ? 'hover:text-gray-300' : ''}`}
              >
                ✕
              </button>
            </div>

            {/* Odpočet – zobrazí se aktivnímu uživateli (pozice 1) */}
            {inQueue && queuePos === 1 && (
              <div className="px-4 py-2 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-700">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-orange-700 dark:text-orange-300">⏱ Čas na výběr</span>
                  <span className={`text-xs font-bold tabular-nums ${queueTimeLeft <= 5 ? 'text-red-600 dark:text-red-400' : 'text-orange-700 dark:text-orange-300'}`}>{queueTimeLeft}s</span>
                </div>
                <div className="w-full bg-orange-200 dark:bg-orange-800 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-1000 ${queueTimeLeft <= 5 ? 'bg-red-500' : 'bg-orange-500'}`}
                    style={{ width: `${Math.round((queueTimeLeft / 20) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Čekací obrazovka – zobrazí se čekajícím uživatelům (pozice 2+) */}
            {inQueue && queuePos > 1 ? (
              <div className="p-8 text-center min-h-[220px] flex flex-col items-center justify-center">
                <div className="text-5xl mb-3">⏳</div>
                <h4 className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Prosím čekejte</h4>
                <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Jste <strong>{queuePos}.</strong> v pořadí
                </p>
                {queueAhead.length > 0 && (
                  <div className={`rounded-lg p-3 text-sm text-left w-full mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className={`font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Aktuálně vybírají:</p>
                    {queueAhead.map((u, i) => (
                      <div key={u.userId} className="flex items-center gap-2 py-1">
                        <span className={i === 0
                          ? (isDarkMode ? 'text-green-400 font-semibold' : 'text-green-700 font-semibold')
                          : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}>
                          {i + 1}. {u.userName}
                        </span>
                        {i === 0 && <span className="text-xs text-orange-500 dark:text-orange-400">zbývá ~{queueTimeLeft}s</span>}
                      </div>
                    ))}
                  </div>
                )}
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Okno se automaticky otevře, až na vás přijde řada.
                </p>
              </div>
            ) : (
            <div className="max-h-96 overflow-y-auto custom-scrollbar p-4">
              {loading && !initialized ? (
                <div className="flex flex-col items-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
                  <p className={`text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Načítám kontakty...
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                    Připravuji dostupné šarže
                  </p>
                </div>
              ) : contactsBatches.length === 0 ? (
                <div className={`text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Žádné dostupné kontakty
                </div>
              ) : (
                contactsBatches.map((batch) => (
                  <div 
                    key={batch.name} 
                    className={`p-3 mb-3 border rounded-lg ${
                      isDarkMode 
                        ? 'border-gray-600 hover:bg-gray-700' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className={`font-medium text-sm flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {batch.name}
                      </h4>
                      {/* Hvězdičky + počet kontaktů – vždy spolu napravo */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <div className="flex flex-col items-end gap-0.5">
                          <div
                            className="flex items-center gap-0 cursor-pointer"
                            onMouseLeave={() => setHoverInfo(null)}
                          >
                            {(() => {
                              const myStars = batchRatings[batch.name]?.[currentUserId]?.stars || 0;
                              const allRatings = Object.values(batchRatings[batch.name] || {});
                              const avg = allRatings.length
                                ? allRatings.reduce((s, r) => s + (r.stars || 0), 0) / allRatings.length
                                : 0;
                              // Hvězdičky vždy zobrazují průměr (nebo moje hodnocení při hoveru)
                              const displayStars = avg;
                              const hoveredStar = hoverInfo?.batch === batch.name ? hoverInfo.star : 0;
                              const othersInfo = allRatings
                                .filter(r => r.userName !== currentUserName)
                                .map(r => `${r.userName}: ${r.stars}★`)
                                .join(', ');
                              const tooltip = allRatings.length > 0
                                ? `Průměr: ${avg.toFixed(1)}/5 (${allRatings.length} hodnocení)${othersInfo ? `\n${othersInfo}` : ''}${myStars ? `\nTvoje: ${myStars}★` : ''}`
                                : 'Ohodnoť tuto várku';
                              return [1,2,3,4,5].map(star => (
                                <button
                                  key={star}
                                  onClick={() => setRating(batch.name, myStars === star ? 0 : star)}
                                  onMouseEnter={() => setHoverInfo({ batch: batch.name, star })}
                                  title={tooltip}
                                  className="text-base leading-none transition-colors focus:outline-none"
                                  style={{
                                    color: hoveredStar > 0
                                      ? star <= hoveredStar
                                        ? '#16a34a'
                                        : (isDarkMode ? '#4b5563' : '#d1d5db')
                                      : star <= displayStars
                                        ? '#f59e0b'
                                        : (isDarkMode ? '#4b5563' : '#d1d5db')
                                  }}
                                >
                                  ★
                                </button>
                              ));
                            })()}
                          </div>
                          {/* Moje hodnocení + průměrný text pod hvězdičkami */}
                          {(() => {
                            const myStars = batchRatings[batch.name]?.[currentUserId]?.stars || 0;
                            const allRatings = Object.values(batchRatings[batch.name] || {});
                            const avg = allRatings.length
                              ? allRatings.reduce((s, r) => s + (r.stars || 0), 0) / allRatings.length
                              : 0;
                            if (allRatings.length === 0) return null;
                            return (
                              <span className={`text-[10px] leading-none ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ø {avg.toFixed(1)} ({allRatings.length}×){myStars > 0 ? ` · já: ${myStars}★` : ''}
                              </span>
                            );
                          })()}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          isDarkMode
                            ? 'bg-blue-900 text-blue-200'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {batch.count} kontaktů
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAssignContacts(batch.name, 1)}
                        disabled={loading || batch.count < 1}
                        className={`flex-1 px-3 py-1 rounded text-xs font-semibold transition ${
                          batch.count >= 1 && !loading
                            ? isDarkMode 
                              ? 'bg-green-700 hover:bg-green-800 text-white' 
                              : 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        +1
                      </button>
                      <button
                        onClick={() => handleAssignContacts(batch.name, 5)}
                        disabled={loading || batch.count < 5}
                        className={`flex-1 px-3 py-1 rounded text-xs font-semibold transition ${
                          batch.count >= 5 && !loading
                            ? isDarkMode 
                              ? 'bg-green-700 hover:bg-green-800 text-white' 
                              : 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        +5
                      </button>
                      <button
                        onClick={() => handleAssignContacts(batch.name, 10)}
                        disabled={loading || batch.count < 10}
                        className={`flex-1 px-3 py-1 rounded text-xs font-semibold transition ${
                          batch.count >= 10 && !loading
                            ? isDarkMode 
                              ? 'bg-green-700 hover:bg-green-800 text-white' 
                              : 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        +10
                      </button>
                      <button
                        onClick={() => handleAssignContacts(batch.name, 20)}
                        disabled={loading || batch.count < 20}
                        className={`flex-1 px-3 py-1 rounded text-xs font-semibold transition ${
                          batch.count >= 20 && !loading
                            ? isDarkMode 
                              ? 'bg-green-700 hover:bg-green-800 text-white' 
                              : 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        +20
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            )}

            <div className={`p-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
              <button
                onClick={handleClose}
                className={`w-full px-3 py-2 rounded text-sm font-semibold transition ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
